// ============================================================================
// Phone bottom bar — which tabs show where (Design Constitution N02, Q09, Table C)
// ============================================================================
// Pure model; src/shared/BottomBar.tsx draws it. Under 640px a bar of up to four
// tabs plus 'More' sits at the bottom of the screen:
//   - in a program: Dashboard, then that program's field sections (Table C
//     "Programs"); More holds only the program's other sections, and hides when
//     there are none;
//   - outside programs and for Light: Home · Dailys · Pasture · More (Q09, the
//     "recommended" and "alternative" rows);
//   - equipment_tech: Home · Machines · Fueling · More;
//   - a page with its own modes (Pasture: Map / Field / Reports) passes them in.
// It hides on field forms, in Pasture Field mode, while a drawer or modal is
// open, signed out, and on the inactive shell (Table C "Where the bar hides").
//
// This module states no access rule of its own. Program sections arrive already
// filtered by deriveNavSelection (src/lib/globalNav.ts), and every other
// destination passes through the caller's `canOpen(view)`. A missing predicate is
// not permission: the bar hides (fail closed, like deriveNavSelection).
// ============================================================================
import type {IconName} from '../shared/Icon.js';
import type {NavSelection} from './globalNav.js';

export type BottomTab = {key: string; label: string; view: string; icon: IconName};

export type BottomBarModel =
  | {hidden: true; reason: BottomBarHideReason}
  | {
      hidden: false;
      kind: 'program' | 'outside' | 'page';
      tabs: BottomTab[];
      more: BottomTab[];
      activeKey: string | null;
    };

export type BottomBarHideReason =
  | 'signed-out'
  | 'inactive'
  | 'field-form'
  | 'pasture-field'
  | 'overlay'
  | 'no-permission-check';

/** Program tab order: Dashboard first, field sections next; the rest go to More (Table C). */
export const PROGRAM_BAR_ORDER: Readonly<Record<string, readonly string[]>> = Object.freeze({
  broiler: Object.freeze(['broilerHome', 'list', 'broilerdailys', 'broilerweighins', 'timeline', 'feed']),
  layer: Object.freeze(['layersHome', 'layerbatches', 'layerdailys', 'eggdailys']),
  pig: Object.freeze(['pigsHome', 'pigbatches', 'pigdailys', 'pigweighins', 'sows', 'farrowing', 'breeding', 'pigs']),
  cattle: Object.freeze([
    'cattleHome',
    'cattleherds',
    'cattledailys',
    'cattleweighins',
    'cattlelog',
    'cattlebatches',
    'cattleforecast',
    'cattlebreeding',
  ]),
  sheep: Object.freeze(['sheepHome', 'sheepflocks', 'sheepdailys', 'sheepweighins', 'sheepbatches']),
});

const SECTION_ICONS: Readonly<Record<string, IconName>> = Object.freeze({
  broilerHome: 'grid',
  layersHome: 'grid',
  pigsHome: 'grid',
  cattleHome: 'grid',
  sheepHome: 'grid',
  list: 'list',
  layerbatches: 'list',
  pigbatches: 'list',
  cattlebatches: 'list',
  sheepbatches: 'list',
  cattleherds: 'users',
  sheepflocks: 'users',
  sows: 'users',
  broilerdailys: 'clipboard',
  layerdailys: 'clipboard',
  eggdailys: 'clipboard',
  pigdailys: 'clipboard',
  cattledailys: 'clipboard',
  sheepdailys: 'clipboard',
  broilerweighins: 'scale',
  pigweighins: 'scale',
  cattleweighins: 'scale',
  sheepweighins: 'scale',
  timeline: 'calendar',
  breeding: 'calendar',
  farrowing: 'calendar',
  cattleforecast: 'calendar',
  cattlebreeding: 'calendar',
  feed: 'grain',
  pigs: 'grain',
  cattlelog: 'book',
});

const tab = (key: string, label: string, view: string, icon: IconName): BottomTab => ({key, label, view, icon});

const HOME = tab('home', 'Home', 'home', 'home');
const DAILYS = tab('dailys', 'Dailys', 'webformhub', 'clipboard');
const PASTURE = tab('pasture', 'Pasture', 'pastureMap', 'pin');
const MACHINES = tab('machines', 'Machines', 'equipmentHome', 'wrench');
const FUELING = tab('fueling', 'Fueling', 'fuelingHub', 'fuel');
const ACTIVITY = tab('activity', 'Activity', 'activity', 'pulse');
const WEATHER = tab('weather', 'Weather', 'weather', 'cloud');
const PROCESSING = tab('processing', 'Processing', 'processing', 'calendar');
const PROJECTS = tab('projects', 'Projects', 'projects', 'list');
const ADMIN = tab('admin', 'Admin', 'webforms', 'gear');

/** More, outside programs (Q09): Activity, Weather, Projects (D9), Processing (operational roles), Admin (admin). */
const OUTSIDE_MORE: readonly BottomTab[] = Object.freeze([ACTIVITY, WEATHER, PROJECTS, PROCESSING, ADMIN]);
/** More for Light (Table C "alternative" row, chosen by Q09; Projects added by D9). */
const LIGHT_MORE: readonly BottomTab[] = Object.freeze([
  tab('addfeed', 'Add Feed', 'addfeed', 'grain'),
  tab('equipment', 'Equipment', 'fuelingHub', 'fuel'),
  tab('pastReports', 'View Past Reports', 'mySubmissions', 'file'),
  tab('cattlelog', 'Cattle Log', 'cattlelog', 'book'),
  PROJECTS,
  WEATHER,
]);
/** More for equipment_tech (Table C; Projects added by D9). */
const TECH_MORE: readonly BottomTab[] = Object.freeze([DAILYS, PASTURE, PROJECTS, ACTIVITY, WEATHER]);

/** Views that light the outside-program bar's tabs; anything else outside a program lights More if listed there. */
const OUTSIDE_ACTIVE: Readonly<Record<string, string>> = Object.freeze({
  home: 'home',
  webformhub: 'dailys',
  pastureMap: 'pasture',
  equipmentHome: 'machines',
  fuelingHub: 'fueling',
  // A40: the Admin equipment record page lives under Admin, which is in More.
  equipmentAdminRecord: 'more',
});

export type BottomBarInput = {
  view: string | null;
  role: string | null;
  /** From deriveNavSelection: sections are already access-filtered. */
  selection: Pick<NavSelection, 'program' | 'sections' | 'activeSectionView'>;
  /** The caller holds the selected program (canAccessProgram). Missing = no program bar. */
  programAccess?: boolean;
  /** The caller's surface-access decision for a view (resolveSurfaceAccess ALLOW). Missing = hide. */
  canOpen?: ((view: string) => boolean) | null;
  /** A page that owns its modes (Pasture) passes them; `activeKey` names the current one. */
  pageTabs?: {tabs: BottomTab[]; activeKey: string | null} | null;
  signedIn: boolean;
  inactive?: boolean;
  fieldForm?: boolean;
  pastureField?: boolean;
  /** A drawer, modal, the header menu or the Bell panel is open. */
  overlayOpen?: boolean;
};

function split(tabs: readonly BottomTab[], view: string | null, more: readonly BottomTab[]) {
  const keyFor = (list: readonly BottomTab[]) => list.find((t) => t.view === view)?.key ?? null;
  const tabKey = keyFor(tabs);
  return {tabs: [...tabs], more: [...more], activeKey: tabKey ?? (keyFor(more) ? 'more' : null)};
}

export function deriveBottomBar(input: BottomBarInput): BottomBarModel {
  if (!input.signedIn) return {hidden: true, reason: 'signed-out'};
  if (input.inactive) return {hidden: true, reason: 'inactive'};
  if (input.fieldForm) return {hidden: true, reason: 'field-form'};
  if (input.pastureField) return {hidden: true, reason: 'pasture-field'};
  if (input.overlayOpen) return {hidden: true, reason: 'overlay'};
  const canOpen = input.canOpen;
  if (typeof canOpen !== 'function') return {hidden: true, reason: 'no-permission-check'};
  const allowed = (list: readonly BottomTab[]) => list.filter((t) => canOpen(t.view));

  if (input.pageTabs && input.pageTabs.tabs.length > 0) {
    return {hidden: false, kind: 'page', tabs: [...input.pageTabs.tabs], more: [], activeKey: input.pageTabs.activeKey};
  }

  const {program, sections} = input.selection;
  const accessibleSections = sections.filter((section) => canOpen(section.view));
  const order = program ? PROGRAM_BAR_ORDER[program] : undefined;
  // A program bar needs the program itself, not just sections: a farm_team
  // caller who reaches a program's Dailys (and Log) only through the daily-report
  // exemption gets the outside bar with Dailys lit (Table C).
  if (order && input.programAccess === true && accessibleSections.length >= 2) {
    const byView = new Map(accessibleSections.map((s) => [s.view, s]));
    const ordered = order
      .filter((v) => byView.has(v))
      .map((v) => tab(v, byView.get(v)!.label, v, SECTION_ICONS[v] ?? 'list'));
    const tabs = ordered.slice(0, 4);
    const more = ordered.slice(4);
    const activeView = input.selection.activeSectionView ?? input.view;
    const activeKey = tabs.some((t) => t.view === activeView)
      ? activeView
      : more.some((t) => t.view === activeView)
        ? 'more'
        : null;
    return {hidden: false, kind: 'program', tabs, more, activeKey};
  }

  const role = input.role;
  const outsideTabs = role === 'equipment_tech' ? allowed([HOME, MACHINES, FUELING]) : allowed([HOME, DAILYS, PASTURE]);
  const outsideMore = allowed(role === 'light' ? LIGHT_MORE : role === 'equipment_tech' ? TECH_MORE : OUTSIDE_MORE);
  const {tabs, more, activeKey} = split(outsideTabs, input.view, outsideMore);
  // A program view reached outside its program bar (farm_team on a program's
  // Dailys) lights Dailys; any other unknown view lights nothing.
  const lit =
    activeKey ??
    (program && input.view && /dailys$/.test(input.view) ? 'dailys' : (OUTSIDE_ACTIVE[input.view || ''] ?? null));
  return {
    hidden: false,
    kind: 'outside',
    tabs,
    more,
    activeKey: lit && (lit === 'more' || tabs.some((t) => t.key === lit)) ? lit : null,
  };
}
